import React from 'react';
import {
  Box,
  Typography,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { getCategoryStyle } from '../../utils/categoryConstants';

// Responsive Services List Component
const ServicesList = ({ services, variant, isOverlapping = false, maxVisible }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  if (!services || services.length === 0) return null;

  // Determine max items based on variant and screen size
  const getMaxItems = () => {
    if (maxVisible) return maxVisible;
    
    // On desktop, show all services for better UX (no truncation)
    if (!isMobile) {
      switch (variant) {
        case 'compact':
        case 'list':
        case 'modal':
          return services.length; // Show all services on desktop
        case 'timeline':
          return isOverlapping ? 6 : services.length;
        case 'detailed':
          return isOverlapping ? 8 : services.length;
        default:
          return services.length;
      }
    }
    
    // Mobile limits to maintain performance and readability
    switch (variant) {
      case 'compact':
        return 3;
      case 'timeline':
        return isOverlapping ? 3 : 6;
      case 'detailed':
        return isOverlapping ? 4 : 8;
      case 'list':
        return 10;
      case 'modal':
        return 12;
      default:
        return 8;
    }
  };

  const maxItems = getMaxItems();
  const visibleServices = services.slice(0, maxItems);
  const remainingCount = services.length - maxItems;

  // Get font size based on variant and screen size
  const getFontSize = () => {
    switch (variant) {
      case 'compact':
        return isMobile ? '0.6rem' : '0.75rem';
      case 'timeline':
        return isOverlapping ? '0.55rem' : (isMobile ? '0.6rem' : '0.7rem');
      case 'detailed':
        return isOverlapping ? (isMobile ? '0.6rem' : '0.65rem') : (isMobile ? '0.7rem' : '0.8rem');
      case 'list':
        return isMobile ? '0.8rem' : '0.85rem';
      case 'modal':
        return isMobile ? '0.85rem' : '0.9rem';
      default:
        return isMobile ? '0.7rem' : '0.8rem';
    }
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      flexWrap: 'wrap', 
      gap: variant === 'modal' ? 0.75 : variant === 'list' ? 0.5 : 0.375, 
      alignItems: 'center',
      // Add max width to force wrapping in constrained table cells
      maxWidth: variant === 'compact' && !isMobile ? '200px' : 
                variant === 'list' && !isMobile ? '280px' : 'none'
    }}>
      {visibleServices.map((service, index) => {
        // Extract category and name from service string or object
        let category = 'Others'; // default
        let displayName = service;
        
        if (typeof service === 'object' && service !== null) {
          // Service is an object with category and name properties from backend
          category = service.category || 'Others';
          displayName = service.custom_name || service.name || service.displayName;
        } else if (typeof service === 'string') {
          // Legacy support for string services - default to Others since we can't reliably infer
          category = 'Others';
          displayName = service;
        }
        
        const categoryStyle = getCategoryStyle({ category });
        
        return (
          <Box
            key={index}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: variant === 'modal' ? 0.5 : 0.375,
              bgcolor: categoryStyle ? `${categoryStyle.color}08` : 'grey.100', // 8% opacity like legend
              borderRadius: 0.5,
              px: variant === 'modal' ? 0.75 : variant === 'list' ? 0.625 : 0.5,
              py: variant === 'modal' ? 0.25 : 0.125,
              border: `1px solid ${categoryStyle ? categoryStyle.color : 'grey'}`,
              // Make chips flexible to available space
              flex: variant === 'modal' || variant === 'list' ? 'none' : '0 1 auto',
              minWidth: variant === 'compact' ? '40px' : '50px', // Minimum readable width
              maxWidth: (() => {
                // Responsive max width based on available space
                if (variant === 'modal' || variant === 'list') return 'none';
                if (variant === 'compact') return isMobile ? '120px' : '180px';
                if (variant === 'timeline') {
                  if (isOverlapping) return '100px';
                  return isMobile ? '140px' : '200px';
                }
                if (variant === 'detailed') {
                  if (isOverlapping) return '120px';
                  return isMobile ? '160px' : '220px';
                }
                return '150px';
              })()
            }}
          >
            {categoryStyle && (
              <Box
                sx={{
                  width: variant === 'compact' ? 10 : variant === 'modal' ? 18 : 12,
                  height: variant === 'compact' ? 10 : variant === 'modal' ? 18 : 12,
                  borderRadius: '50%',
                  bgcolor: categoryStyle.color, // Full color background like legend
                  color: 'white', // White text like legend
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  fontSize: variant === 'compact' ? '0.5rem' : variant === 'modal' ? '0.75rem' : '0.6rem',
                  fontWeight: 'bold'
                }}
              >
                {categoryStyle.letter}
              </Box>
            )}
            <Typography
              variant="caption"
              sx={{
                fontSize: getFontSize(),
                fontWeight: variant === 'modal' ? 600 : 500,
                color: 'text.primary',
                lineHeight: variant === 'modal' ? 1.2 : 1,
                // Dynamic width based on available space
                flex: variant === 'modal' || variant === 'list' ? 'none' : '1 1 auto',
                minWidth: 0, // Allow flex item to shrink below content width
                maxWidth: (() => {
                  // Dynamic maxWidth based on variant and context
                  if (variant === 'modal' || variant === 'list') return 'none';
                  if (variant === 'compact') return isMobile ? '80px' : 'none';
                  if (variant === 'timeline') {
                    if (isOverlapping) return '60px';
                    return isMobile ? '100px' : '120px';
                  }
                  if (variant === 'detailed') {
                    if (isOverlapping) return '80px';
                    return isMobile ? '120px' : '150px';
                  }
                  return '100px';
                })(),
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {displayName}
            </Typography>
          </Box>
        );
      })}
      
      {remainingCount > 0 && (
        <Typography
          variant="caption"
          sx={{
            fontSize: getFontSize(),
            color: 'text.secondary',
            fontWeight: 'bold'
          }}
        >
          +{remainingCount}
        </Typography>
      )}
    </Box>
  );
};

export default ServicesList;